/**
 * Low-level axios base query for RTK Query.
 *
 * Responsibilities:
 *  - Prefix every request with the validated API base URL.
 *  - Attach the in-memory bearer token (never from localStorage).
 *  - Send the resolved app mode (cca/eia) as a header so ONE backend contract can
 *    serve both variants without separate endpoints.
 *  - Enforce a request timeout; surface axios errors in RTK Query's
 *    { error: { status, data } } shape so interceptors.js doesn't need to know
 *    the request layer is axios rather than fetch.
 */
import axios from 'axios';

import { env } from '@config/env';
import { getVariant, resolveAppMode } from '@variant/appMode';
import { getAccessToken } from '@security/auth/tokenStore';

const axiosInstance = axios.create({
  baseURL: env.apiBaseUrl,
  timeout: env.apiTimeoutMs,
  /*
   * Credentials are OFF, matching legacy (apiservice.js sets no withCredentials
   * on any of its instances).
   *
   * This was `true`, for an httpOnly refresh cookie that does not exist yet. The
   * cost of leaving it on is real: a credentialed cross-origin request forbids
   * `Access-Control-Allow-Origin: *`, so if the backend replies with a wildcard
   * the browser discards an otherwise fine response and reports it as a CORS
   * failure. Authorisation here is the bearer token, not a cookie — so asking
   * for cookies buys nothing and can only break the request.
   *
   * The token refresh (services/sessionRefresh.js) needs no cookies either: it
   * sends the refresh token in its body, as legacy does.
   */
  withCredentials: false,
});

/**
 * The `Appname` value the backend expects: `CCA` for cca, `EIA` for eia.
 *
 * Read from the variant's own `shortName` rather than upper-casing the mode, so
 * the header is tied to variants.config.js — the single source of truth for what
 * each product is called — instead of silently depending on the mode key
 * happening to be the lower-cased product name.
 *
 * @param {'cca' | 'eia'} [mode]
 * @returns {'CCA' | 'EIA'}
 */
export function appNameFor(mode = resolveAppMode()) {
  return getVariant(mode).shortName;
}

axiosInstance.interceptors.request.use((config) => {
  const token = getAccessToken();
  const mode = resolveAppMode();

  config.headers.set('Accept', 'application/json');
  if (token) config.headers.set('Authorization', `Bearer ${token}`);

  // Two mode headers, on purpose:
  //  - X-Advisory-Mode is this app's own contract.
  //  - Appname is what the EXISTING backend actually reads (legacy sets
  //    `Appname: CCA|EIA` on every authed axios instance). Several endpoints
  //    additionally want app_name/appName in the params or body; those stay
  //    with the endpoint that needs them rather than being forced on here.
  //
  // Opt-out: a request whose args carry `skipModeHeader: true` goes without
  // X-Advisory-Mode (Appname is always sent). Used by Analytics v2 only.
  // Legacy parity: apiservice.js:44-50 — legacy's analyticsBaseURL / CSbaseURL
  // send only Authorization + Appname.
  if (!config.skipModeHeader) config.headers.set('X-Advisory-Mode', mode);
  config.headers.set('Appname', appNameFor(mode));

  return config;
});

/**
 * @param {object}  args
 * @param {number}  [args.timeout]        per-request axios timeout in ms; `0` = none.
 *                                        Omitted = the default (env.apiTimeoutMs).
 * @param {boolean} [args.skipModeHeader] leave out X-Advisory-Mode (see above).
 *
 * Both are opt-in, so every other caller keeps the defaults. The error carries
 * two extra fields beside RTK's { status, data }: `code` (axios's err.code,
 * e.g. 'ERR_NETWORK') and `sent` (the request went out but no response came
 * back) — enough for a caller to tell legacy's no-response cases apart
 * (Analytics v2's legacyErrorToast.js). normalizeError ignores them.
 */
export const rawBaseQuery = async ({
  url,
  method = 'GET',
  body,
  params,
  headers,
  timeout,
  skipModeHeader,
}) => {
  try {
    const response = await axiosInstance.request({
      url,
      method,
      data: body,
      params,
      headers,
      // Legacy parity: apiservice.js:44-50 / :15-21 — the analytics axios
      // instances have no timeout; Analytics v2 passes `timeout: 0`.
      ...(timeout !== undefined && { timeout }),
      ...(skipModeHeader && { skipModeHeader: true }),
    });
    return { data: response.data };
  } catch (err) {
    if (!err.response) {
      return {
        error: {
          status: err.code === 'ECONNABORTED' ? 'TIMEOUT_ERROR' : 'FETCH_ERROR',
          data: err.message,
          code: err.code,
          sent: Boolean(err.request),
        },
      };
    }
    return { error: { status: err.response.status, data: err.response.data } };
  }
};
